import { container } from 'tsyringe'
import Vue from 'vue'
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators'
import store from '@/app/ui/store'
import { PayrollPresenter } from '@/app/ui/presenters/PayrollPresenter'
import {
  CreatePayrollApiRequest,
  UpdatePeriodBasicFeeApiRequest,
  UpdatePeriodBonusFeeApiRequest,
  UpdatePeriodWithdrawApiRequest,
} from '@/data/payload/api/PayrollRequest'
import { EventBus, EventBusConstants } from '@/app/infrastructures/misc'
import { Utils } from '@/app/infrastructures/misc'
import { Payrolls, Payroll, PeriodRule } from '@/domain/entities/Payroll'
import { Pagination } from '@/domain/entities/Pagination'

export interface PayrollPeriodPayload {
  startDate: string
}

export interface FundsWithdrawalPayload {
  min_payout_amount?: number
  min_saldo?: number
  payout_fee?: number
}

export interface BasicFeePayload {
  calculation_method: string[]
  multiply_type: string[]
  product_type: string[]
  rules: RulePayload[]
}

export interface RulePayload {
  calculation_method?: string
  delivery_tier?: TierPayload[]
  pickup_tier?: TierPayload[]
  product_type?: string
}

export interface BonusFeePayload {
  bonus_recipient?: string[],
  captain_bonus?: string[],
  captain_bonus_fee?: number,
  captain_bonus_options?: string[],
  insurance?: boolean,
  performance_bonus?: boolean,
  performance_bonus_data?: BonusDataPayload[],
  percentage_bonus?: boolean,
  percentage_bonus_data?: BonusPercentagePayload[],
  pickup_bonus?: boolean,
  pickup_bonus_data?: BonusDataPayload[],
}

export interface BonusDataPayload {
  min_stt: number,
  bonus: number
}

export interface BonusPercentagePayload {
  adjustment: number,
  bonus_max_limit:  number,
  percentage: number
}

export interface TierPayload {
  fee?: number
  min_weight?: number
  multiply_type?: string
  parking_fee?: number
}

interface PayrollState {
  isLoading: boolean
  isChanged: boolean
  dataPayrollList: Payroll[]
  dataPayroll: Payroll
  dataPeriodRule: PeriodRule
  paginationData: Pagination
}

@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: 'payroll',
})
class PayrollController extends VuexModule implements PayrollState {
  private presenter: PayrollPresenter = container.resolve(PayrollPresenter)
  public isLoading = false
  public isChanged = false
  public dataPayrollList = [new Payroll()]
  public dataPayroll = new Payroll()
  public dataPeriodRule = new PeriodRule()
  public paginationData = new Pagination()

  @Action({ rawError: true })
  public getAll(params: Record<string, string | number>) {
    this.setLoading(true)

    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getAll(formattedParams)
      .then(res => {
        if (res.data) this.setDataPayrollList(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Period List Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getDetail(id: string) {
    this.setLoading(true)

    this.presenter
      .get(id)
      .then(res => {
        if (res) {
          this.setDataPeriodRule(res)
        }
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Detail Period Rule Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public create(payload: PayrollPeriodPayload) {
    this.setLoading(true)
    this.presenter
      .create(new CreatePayrollApiRequest(payload.startDate))
      .then(res => {
        EventBus.$emit(EventBusConstants.ADD_PAYROLL_SUCCESS, res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Create Payroll Period Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
        EventBus.$emit(EventBusConstants.ADD_PAYROLL_SUCCESS, false)
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public updatePeriodWithdraw(payload: {
    id: string
    rules: FundsWithdrawalPayload
  }) {
    this.setLoading(true)
    this.presenter
      .update(
          payload.id,
        new UpdatePeriodWithdrawApiRequest(
          payload.rules
        )
      )
      .then((res) => {
        EventBus.$emit(EventBusConstants.EDIT_PERIOD_WITHDRAW_SUCCESS, res)
      })
      .catch(error => {
        EventBus.$emit(EventBusConstants.EDIT_PERIOD_WITHDRAW_SUCCESS, false)
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public updatePeriodBasicFee(payload: { id: string; rules: BasicFeePayload }) {
    this.setLoading(true)
    this.presenter
      .update(payload.id, new UpdatePeriodBasicFeeApiRequest(payload.rules))
      .then(res => {
        EventBus.$emit(EventBusConstants.EDIT_PERIOD_BASIC_FEE_SUCCESS, res)
      })
      .catch(error => {
        EventBus.$emit(EventBusConstants.EDIT_PERIOD_BASIC_FEE_SUCCESS, false)
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public updatePeriodBonusFee(payload: { id: string; rules: BonusFeePayload }) {
    this.setLoading(true)
    this.presenter
      .update(payload.id, new UpdatePeriodBonusFeeApiRequest(payload.rules))
      .then(res => {
        EventBus.$emit(EventBusConstants.EDIT_PERIOD_BONUS_SUCCESS, res)
      })
      .catch(error => {
        EventBus.$emit(EventBusConstants.EDIT_PERIOD_BONUS_SUCCESS, false)
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Mutation
  public setIsChanged(bool: boolean) {
    this.isChanged = bool
  }

  @Mutation
  private setLoading(bool: boolean) {
    this.isLoading = bool
  }

  @Mutation
  private setDataPayrollList(list: Payrolls) {
    this.dataPayrollList = <Payroll[]>list.data
    this.paginationData = <Pagination>list.pagination
  }

  @Mutation
  private setDataPeriodRule(list: PeriodRule) {
    this.dataPeriodRule = list
  }
}

export default getModule(PayrollController)
